import {
    createContext,
    PropsWithChildren,
    useContext,
    useState,
    useEffect
} from "react";

import { GrowthUnifiedProfileResponse } from "@/patterns/return-to-product-modal/src/return-to-product-modal-v2";
import { useGupsStatus } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks";

type SubanchorContext = {
    subAnchor?: string;
};

const SubanchorContext = createContext<SubanchorContext | null>(null);

export const useSubanchorContext = () => {
    const context = useContext(SubanchorContext);
    if (!context) {
        throw new Error(
            "useSubanchorContext must be used within a SubanchorProvider"
        );
    }
    return context;
};

export const SubanchorProvider = ({ children }: PropsWithChildren) => {
    const { getProfileCallback } = useGupsStatus();
    const [subAnchor, setSubAnchor] = useState<string | undefined>("");

    useEffect(() => {
        const abortController = new AbortController();

        const getData = async () => {
            try {
                const { data, error } = (await getProfileCallback(
                    abortController.signal
                )) as GrowthUnifiedProfileResponse;

                // Check if component is still mounted before updating state
                if (abortController.signal.aborted) {
                    return;
                }

                if (error) {
                    console.log(`gups profile error ${error}`);
                }
                if (data) {
                    setSubAnchor(
                        data?.paidChannelContext?.confluence?.subAnchor
                    );
                }
            } catch (error) {
                // Don't log AbortErrors as they're expected when component unmounts
                if (error instanceof Error && error.name !== "AbortError") {
                    console.error("Error getting GUPS profile:", error);
                }
            }
        };

        getData();

        // Cleanup function to abort request if component unmounts
        return () => {
            abortController.abort();
        };
    }, [getProfileCallback]);

    return (
        <SubanchorContext.Provider
            value={{
                subAnchor
            }}
        >
            {children}
        </SubanchorContext.Provider>
    );
};
